/**
 * Quick add modal.
 *
 * Fills the quick view modal with the purchase blocks of the real product page
 * (custom properties, clan name, variant picker, personalisation, buy buttons)
 * so the customer goes through the same required-option checks as on the
 * product page. Whenever the blocks cannot be reproduced faithfully the
 * customer is sent to the product page instead of an incomplete form.
 */
(function () {
  // Block types carried over from the product page (.main-product__block-<type>).
  const KEEP_BLOCKS = [
    "title",
    "price",
    "badge",
    "custom-properties",
    "clan_name",
    "custom_field",
    "image_field",
    "variant_picker",
    "buy_buttons",
    "collapsible_tab"
  ];
  const ID_REFERENCES = ["for", "form", "aria-controls", "aria-labelledby", "aria-describedby"];
  const SUFFIX = "-qa";
  // Prefetched pages are reused for a while; prices and stock are re-read by
  // the variant picker from /products/<handle>.js on every open anyway.
  const CACHE_TTL = 5 * 60 * 1000;
  const cache = new Map();

  function fetchPage(url) {
    return fetch(url, { credentials: "same-origin" }).then((response) => {
      if (!response.ok) throw new Error(`Failed to load product page: ${url}`);
      return response.text();
    });
  }

  function productUrlFor(handle) {
    return `${window.MinimogSettings.base_url}products/${handle}`;
  }

  // Raw HTML is cached, not DOM: every open parses a fresh copy.
  function loadProduct(productUrl) {
    const hit = cache.get(productUrl);
    if (hit && Date.now() - hit.time < CACHE_TTL) return hit.promise;

    const promise = Promise.all([fetchPage(`${productUrl}?section_id=product-quickview`), fetchPage(productUrl)]);
    cache.set(productUrl, { time: Date.now(), promise });
    promise.catch(() => {
      if (cache.get(productUrl)?.promise === promise) cache.delete(productUrl);
    });
    return promise;
  }

  // Start downloading as soon as the customer points at a quick add button,
  // so the modal is usually ready by the time they click.
  function prefetch(event) {
    if (navigator.connection?.saveData || !window.MinimogSettings) return;
    const button = event.target.closest?.(".sf-pqv__button[data-product-handle]");
    if (!button) return;
    loadProduct(productUrlFor(button.dataset.productHandle)).catch(() => {});
  }

  // The shell is parsed in an inert document on purpose: on product pages
  // <media-gallery> is already defined, and parsing straight into the page would
  // start it before the product form is mounted. It then finds no variant input,
  // gives up, and the image slider never starts.
  function parseShell(html) {
    const shell = new DOMParser().parseFromString(html, "text/html").querySelector(".sf-pqv");
    if (!shell) throw new Error("Quick view section not found");
    return shell;
  }

  function isKeptBlock(node) {
    if (!node.classList.contains("main-product__block")) return false;
    return KEEP_BLOCKS.some((type) => node.classList.contains(`main-product__block-${type}`));
  }

  // Pull the purchase blocks out of the product page HTML, re-keyed to the
  // quick view section so they bind to the modal instead of the page behind it.
  function buildBlocks(pageHtml, targetSectionId) {
    const doc = new DOMParser().parseFromString(pageHtml, "text/html");
    const section = doc.querySelector('[data-section-type="product-page"]');
    const source = section?.querySelector(".main-product");
    const list = source?.querySelector(".main-product__blocks");
    if (!list) throw new Error("Product blocks not found");

    const pageSectionId = section.dataset.sectionId;
    const formId = `product-form-${pageSectionId}`;

    Array.from(list.children).forEach((node) => {
      if (isKeptBlock(node)) return;
      // A dropped block that feeds the product form (app block, form-linked
      // field) would let an incomplete order through.
      const isAppBlock = node.classList.contains("main-product__block-@app");
      const feedsForm = node.querySelector(`[name^="properties["], [form="${formId}"]`);
      if (isAppBlock || feedsForm) throw new Error("Product needs options the modal cannot show");
      node.remove();
    });

    if (!source.querySelector(`form#${CSS.escape(formId)}`)) throw new Error("Product form not found");

    const assets = {
      styles: Array.from(section.querySelectorAll('link[rel="stylesheet"][href*="dropdown-filter"]')).map((node) =>
        node.getAttribute("href")
      ),
      scripts: Array.from(section.querySelectorAll('script[src*="dropdown-filter"]')).map((node) =>
        node.getAttribute("src")
      )
    };

    // Only Add to cart goes through the required-option checks.
    source.querySelectorAll(".prod__dynamic_checkout, pickup-availability, link, script[src]").forEach((node) => node.remove());

    // The modal must not rewrite the URL of the page behind it.
    source.dataset.enableHistoryState = "false";
    source.querySelectorAll("variant-picker").forEach((picker) => picker.setAttribute("data-update-url", "false"));

    const template = document.createElement("template");
    template.innerHTML = source.outerHTML.split(pageSectionId).join(targetSectionId);
    return { blocks: template.content.firstElementChild, assets };
  }

  // The same product markup may already be on the page (quick add opened from a
  // product page), so ids and ownerless radio groups have to stay unique.
  function dedupe(root) {
    const renamed = {};
    // JSON data scripts (#productVariants, #variantGroup) keep their ids: the
    // variant picker looks them up by id inside its own container.
    root.querySelectorAll("[id]:not(script)").forEach((node) => {
      if (!document.getElementById(node.id)) return;
      renamed[node.id] = node.id + SUFFIX;
      node.id = renamed[node.id];
    });

    ID_REFERENCES.forEach((attribute) => {
      root.querySelectorAll(`[${attribute}]`).forEach((node) => {
        const value = node.getAttribute(attribute);
        if (renamed[value]) node.setAttribute(attribute, renamed[value]);
      });
    });

    const groups = {};
    root.querySelectorAll('input[type="radio"][name]:not([form])').forEach((radio) => {
      if (radio.closest("form")) return;
      if (!(radio.name in groups)) {
        groups[radio.name] = !!document.querySelector(`input[type="radio"][name="${CSS.escape(radio.name)}"]`);
      }
      if (groups[radio.name]) radio.name += SUFFIX;
    });
  }

  // Scripts inserted through innerHTML never run; re-create them once attached.
  function runInlineScripts(root) {
    root.querySelectorAll("script:not([src])").forEach((oldScript) => {
      const type = oldScript.getAttribute("type");
      if (type && type !== "text/javascript") return;
      const script = document.createElement("script");
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function bindSizeChart(root) {
    root.querySelectorAll('[data-trigger="modal"]').forEach((button) => {
      const chart = root.querySelector(button.getAttribute("data-target"));
      if (!chart) return;
      const toggle = (open) => {
        chart.classList.toggle("modal--active", open);
        button.setAttribute("aria-expanded", String(open));
      };
      toggle(false);
      button.addEventListener("click", (event) => {
        event.preventDefault();
        toggle(!chart.classList.contains("modal--active"));
      });
      chart.querySelectorAll(".modal__cancel, .modal__backdrop").forEach((node) => {
        node.addEventListener("click", () => toggle(false));
      });
    });
  }

  function loadStyle(href) {
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // Custom property dropdowns: dropdown-filter.js builds the visible dropdown
  // and releases `required` on selects that have no option for this product.
  function initDropdowns(root, assets) {
    const selects = Array.from(root.querySelectorAll("select.dropdown-filter-option"));
    if (!selects.length) return Promise.resolve();
    assets.styles.forEach(loadStyle);

    let ready;
    if (typeof window.create_custom_dropdowns === "function") {
      window.create_custom_dropdowns();
      ready = Promise.resolve();
    } else {
      // The script builds the dropdowns itself once it has loaded.
      ready = Promise.all(assets.scripts.map(loadScript));
    }

    return ready.then(() => {
      // A required select the customer cannot see would lock the form for good.
      const stuck = selects.some((select) => {
        const built = select.nextElementSibling?.classList.contains("dropdown-select");
        const hidden = select.closest(".main-product__block")?.classList.contains("is-empty");
        return select.required && (!built || hidden);
      });
      if (stuck) throw new Error("Custom dropdowns failed to initialise");

      // The modal column is short: bring an opened list into view. Deferred
      // because dropdown-filter.js toggles `open` from a document listener.
      root.addEventListener("click", (event) => {
        const dropdown = event.target.closest?.(".dropdown-select");
        if (!dropdown) return;
        setTimeout(() => {
          if (!dropdown.classList.contains("open")) return;
          dropdown.querySelector(".list")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      });
    });
  }

  // Same open/close behaviour as the product page accordions (app.js only binds
  // the ones present at page load).
  function bindAccordions(root) {
    root.querySelectorAll(".prod__accordion .sf__accordion-item").forEach((item) => {
      const content = item.querySelector(".sf__accordion-content");
      if (!content) return;
      content.style.opacity = 1;
      content.classList.toggle("overflow-hidden", !item.classList.contains("open"));
    });
    root.addEventListener("click", (event) => {
      const button = event.target.closest?.(".prod__accordion .sf__accordion-button");
      if (!button) return;
      const item = button.closest(".sf__accordion-item");
      const content = item?.querySelector(".sf__accordion-content");
      if (!content) return;
      const isOpen = item.classList.toggle("open");
      item.style.setProperty("--content-max-height", isOpen ? `${content.scrollHeight}px` : "0px");
      if (isOpen) {
        setTimeout(() => content.classList.remove("overflow-hidden"), 350);
      } else {
        content.classList.add("overflow-hidden");
      }
    });
  }

  function activate(blocks, assets) {
    runInlineScripts(blocks);
    bindAccordions(blocks);
    // The required-option guard ships with the variant picker; without it the
    // form would accept an incomplete selection.
    if (blocks.querySelector("[data-variant-picker]") && !window.__mmRequireVariantInit) {
      return Promise.reject(new Error("Required-option guard not installed"));
    }
    bindSizeChart(blocks);
    return initDropdowns(blocks, assets);
  }

  function mount(shell, blocks, productUrl) {
    const info = shell.querySelector(".quick-view__info");
    if (!info) throw new Error("Quick view layout not found");

    const link = document.createElement("a");
    link.className = "quick-add__details block px-4 mt-4 underline";
    link.href = productUrl;
    link.textContent = info.dataset.viewDetails || "View details";

    info.textContent = "";
    info.append(blocks, link);
  }

  function open(quickView, productHandle) {
    const { MinimogThemeStyles, MinimogThemeScripts } = window;
    const productUrl = productUrlFor(productHandle);
    const fallback = (error) => {
      console.error(error);
      window.location.href = productUrl;
    };

    loadAssetsNew([MinimogThemeStyles.product], "quick-view-assets");
    loadProduct(productUrl)
      .then(([shellHtml, pageHtml]) => {
        const shell = parseShell(shellHtml);
        const { blocks, assets } = buildBlocks(pageHtml, shell.dataset.sectionId);
        dedupe(blocks);
        mount(shell, blocks, productUrl);

        quickView.modalContent = shell;
        quickView.mediaGallery = shell.querySelector("media-gallery");

        loadAssetsNew([MinimogThemeScripts.productMedia, MinimogThemeScripts.variantsPicker], "variants-picker", () => {
          quickView.modal.appendChild(shell);
          quickView.modal.setWidth("1200px");
          quickView.modal.setSizes("sf-modal__quickview");
          quickView.modal.open();
          quickView.toggleLoading(quickView.target, false);
          quickView.isOpen = true;
          activate(blocks, assets).catch(fallback);
        });

        if (shell.querySelector("product-model")) {
          loadAssetsNew(
            [
              MinimogThemeScripts.productModel,
              "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css"
            ],
            "product-model-assets"
          );
        }
      })
      .catch(fallback);
  }

  function install() {
    const quickView = window.MinimogTheme?.ProductQuickView;
    if (!quickView || quickView.quickAddInstalled) return !!quickView;
    quickView.quickAddInstalled = true;
    quickView.fetchHtml = (productHandle) => open(quickView, productHandle);
    return true;
  }

  document.addEventListener("pointerover", prefetch, { passive: true });
  document.addEventListener("focusin", prefetch);
  document.addEventListener("touchstart", prefetch, { passive: true });

  // quick-view.min.js can be loaded on demand (wishlist page), so keep trying
  // until the first quick add click.
  if (!install()) {
    document.addEventListener("DOMContentLoaded", install);
    document.addEventListener(
      "click",
      (event) => {
        if (event.target.closest?.(".sf-pqv__button")) install();
      },
      true
    );
  }
})();
